// frontend/src/components/common/PasswordStrengthMeter.js
import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

const criteria = [
  { label: '8+ characters', test: (pw) => pw.length >= 8 },
  { label: 'Lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'Uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'Special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

const levels = [
  { label: 'Very Weak', color: '#d32f2f' },
  { label: 'Weak', color: '#f44336' },
  { label: 'Fair', color: '#ff9800' },
  { label: 'Good', color: '#2196f3' },
  { label: 'Strong', color: '#4caf50' },
];

export default function PasswordStrengthMeter({ password = '' }) {
  const { score, met } = useMemo(() => {
    if (!password) return { score: 0, met: [] };
    const met = criteria.map((c) => c.test(password));
    const score = met.filter(Boolean).length;
    return { score, met };
  }, [password]);

  if (!password) return null;

  const level = levels[Math.max(score - 1, 0)];
  const progress = (score / criteria.length) * 100;

  return (
    <Box sx={{ mt: 0.5, mb: 0.5 }} role="group" aria-label="Password strength">
      <LinearProgress
        variant="determinate"
        value={progress}
        aria-label={`Password strength: ${level.label}`}
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={criteria.length}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            bgcolor: level.color,
            borderRadius: 2,
          },
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
        <Typography variant="caption" sx={{ color: level.color, fontWeight: 500 }}>
          {level.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {score}/{criteria.length}
        </Typography>
      </Box>
      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
        {criteria.map((c, i) => (
          <Typography
            key={c.label}
            variant="caption"
            sx={{
              color: met[i] ? 'success.main' : 'text.disabled',
              fontSize: '0.7rem',
              '&::before': { content: met[i] ? '"\\2713 "' : '"\\2717 "' },
            }}
          >
            {c.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
